import { bracketToDotNotation } from './string';

/**
 * Represents an error response from the API.
 */
export interface ResponseErrorType {
  code: string;
  message: string;
  missing_permissions?: string[];
  // `source` mixes scalar transport metadata (e.g. `location: "body"`, `pointer`)
  // with the `key` map of field errors, so values can be strings or nested maps.
  source?: Record<string, Record<string, string> | string>;
  requirements?: Record<string, string>;
  status?: number;
  /** Identifier of the operation that failed (e.g. a server action path). */
  action?: string;
}

export interface ErrorDetails {
  code?: string;
  message: string;
  missingPermissions?: string[];
  status?: number;
  action?: string;
}

export interface ResponseErrorDetails {
  code?: string;
  message?: string;
  payload: unknown;
  status?: number;
}

function getRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : undefined;
}

function getNumericValue(value: unknown): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function getStringArrayValue(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : undefined;
}

export function getResponseErrorPayload(value: unknown): unknown {
  const record = getRecord(value);
  return record && 'data' in record ? record.data : value;
}

export function getResponseErrorDetails(value: unknown): ResponseErrorDetails {
  const payload = getResponseErrorPayload(value);
  const outerRecord = getRecord(value);
  const payloadRecord = getRecord(payload);
  const nestedError = getRecord(payloadRecord?.error);

  return {
    code: getStringValue(nestedError?.code) ?? getStringValue(payloadRecord?.code),
    message: getStringValue(nestedError?.message) ?? getStringValue(payloadRecord?.message),
    payload: nestedError ?? payload,
    status: getNumericValue(outerRecord?.status)
      ?? getNumericValue(payloadRecord?.status)
      ?? getNumericValue(nestedError?.status),
  };
}

export function getResponseStatus(value: unknown, fallback?: number): number | undefined {
  return getResponseErrorDetails(value).status ?? fallback;
}

/**
 * Extracts and formats error source messages from a ResponseErrorType.
 * Returns a formatted string with each source key-value pair on a new line.
 */
export const getErrorSourceMessage = (error: ResponseErrorType): string => {
  if (!error.source) return '';

  return Object.entries(error.source).reduce((acc, [source, entries]) => {
    // Only object-valued entries are field-error maps. Skip scalar metadata
    // (e.g. `location: "body"`) so we don't iterate a string into per-character
    // lines (`location.0: b`, `location.1: o`, ...).
    if (typeof entries !== 'object' || entries === null) return acc;

    return Object.entries(entries).reduce((innerAcc, [key, value]) => {
      const label = source === 'key' ? key : `${source}.${key}`;
      return `${innerAcc}\n• ${label}: ${value}`;
    }, acc);
  }, '');
};

/**
 * Extracts and formats error requirements messages from a ResponseErrorType.
 * Returns a formatted string with each requirement key-value pair on a new line.
 */
export const getErrorRequirementsMessage = (error: ResponseErrorType): string => {
  return error.requirements
    ? Object.keys(error.requirements).reduce((acc, key) => {
        return `${acc}\n• ${key}: ${error.requirements?.[key]}`;
      }, '')
    : '';
};

/**
 * Formats a complete error message from a ResponseErrorType.
 * Combines the main error message with source and requirement details if available.
 */
export const getErrorMessage = (error: ResponseErrorType): string => {
  const errorSources = getErrorSourceMessage(error);
  const errorRequirements = getErrorRequirementsMessage(error);

  if (!errorSources && !errorRequirements) {
    return error.message;
  }

  return `${error.message}:${errorSources}${errorRequirements}`;
};

/**
 * Normalizes an unknown thrown value into UI-safe error details.
 *
 * API errors are often thrown as plain objects, while browser/runtime failures
 * are usually Error instances. This keeps that runtime handling in one shared
 * place so components can render a consistent shape.
 */
function getAttachedErrorFields(record: Record<string, unknown> | undefined) {
  return {
    code: getStringValue(record?.code),
    status: getNumericValue(record?.status),
    missingPermissions: getStringArrayValue(record?.missing_permissions),
    action: getStringValue(record?.action),
  };
}

export const getErrorDetails = (
  error: unknown,
  defaultMessage = 'An unexpected error occurred.',
): ErrorDetails => {
  if (error instanceof Error) {
    // Errors can carry API response fields as own-properties (e.g. when a
    // thrown API error object has been normalized into an Error), so surface
    // those alongside the standard message/name.
    const attached = getAttachedErrorFields(getRecord(error));

    return {
      ...(error.name !== 'Error' ? { code: error.name } : attached.code ? { code: attached.code } : {}),
      message: error.message || defaultMessage,
      ...(attached.missingPermissions ? { missingPermissions: attached.missingPermissions } : {}),
      ...(attached.status != null ? { status: attached.status } : {}),
      ...(attached.action ? { action: attached.action } : {}),
    };
  }

  const responseError = getResponseErrorDetails(error);
  const payloadRecord = getRecord(responseError.payload);
  const message = responseError.message ?? defaultMessage;
  const apiError: ResponseErrorType = {
    ...(payloadRecord ?? {}),
    code: responseError.code ?? 'UNKNOWN_ERROR',
    message,
    status: responseError.status,
  };
  const missingPermissions = getStringArrayValue(payloadRecord?.missing_permissions);
  const action = getStringValue(payloadRecord?.action);

  return {
    ...(responseError.code ? { code: responseError.code } : {}),
    message: getErrorMessage(apiError),
    ...(missingPermissions ? { missingPermissions } : {}),
    ...(responseError.status != null ? { status: responseError.status } : {}),
    ...(action ? { action } : {}),
  };
};

/**
 * Normalizes an unknown thrown value into a real `Error` instance.
 *
 * API errors are frequently thrown as plain objects. Passing those straight to
 * an error reporter (e.g. `Sentry.captureException`) yields an unhelpful
 * "Object captured as exception with keys: ..." title that buries the real
 * message. Converting to an `Error` first makes the message the reported title
 * and preserves the structured fields (status, missing permissions, failing
 * endpoint) as own-properties for downstream context.
 */
export const normalizeToError = (error: unknown, defaultMessage?: string): Error => {
  if (error instanceof Error) {
    return error;
  }

  const details = getErrorDetails(error, defaultMessage);
  const normalized = new Error(details.message);
  if (details.code) {
    normalized.name = details.code;
  }

  return Object.assign(normalized, {
    ...(details.status != null ? { status: details.status } : {}),
    ...(details.missingPermissions ? { missing_permissions: details.missingPermissions } : {}),
    ...(details.action ? { action: details.action } : {}),
  });
};

/**
 * Parses API error response into a map of field paths to error messages.
 * Useful for displaying inline validation errors on form fields.
 *
 * Converts bracket notation to dot notation for array indices to match
 * React Hook Form's path format (e.g., `items[0].name` -> `items.0.name`).
 *
 * @param error - The API error response
 * @returns A record mapping field paths to error messages, or null if no field errors
 */
export const parseApiFieldErrors = (error: ResponseErrorType): Record<string, string> | null => {
  const keyErrors = error?.source?.key;
  if (!keyErrors || typeof keyErrors !== 'object') return null;

  return Object.entries(keyErrors).reduce(
    (acc, [path, message]) => {
      acc[bracketToDotNotation(path)] = message;
      return acc;
    },
    {} as Record<string, string>
  );
};
